import {
	createActionsHook,
	createHook,
	createStateHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type HookStateFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import type { ConversationAction, CustomFollowUpContext } from '../../common/types';

type ConversationActionStore = {
	actionFilter?: (actions: ConversationAction[]) => ConversationAction[];
	actions: ConversationAction[];
	input?: string;
	activeAction?: ConversationAction;
	buttonClickedTimestamp?: number;

	/*
	 * Jira Issue View does not use the provider pattern yet, so we need to skip the provider action fetching.
	 * When Jira Issue View is migrated to set actions using provider pattern, we can remove this store.
	 */
	jiraIssueViewActions?: ConversationAction[];
};

const store = createStore({
	initialState: {
		actionFilter: undefined,
		actions: [],
		jiraIssueViewActions: undefined,
		input: '',
		activeAction: undefined,
		buttonClickedTimestamp: undefined,
	} as ConversationActionStore,
	actions: {
		setConversationActions:
			(
				actions: ConversationAction[],
			): (({ setState }: StoreActionApi<ConversationActionStore>) => void) =>
			({ setState }) => {
				setState({
					actions: actions,
				});
			},
		/*
		 * Jira Issue View does not use the provider pattern yet, so we need to skip the provider action fetching.
		 * When Jira Issue View is migrated to set actions using provider pattern, we can remove this setter.
		 */
		setJiraIssueViewActions:
			(
				actions: ConversationAction[],
			): (({ setState }: StoreActionApi<ConversationActionStore>) => void) =>
			({ setState }) => {
				setState({
					jiraIssueViewActions: actions,
				});
			},
		setInput:
			(input: string): (({ setState }: StoreActionApi<ConversationActionStore>) => void) =>
			({ setState }) => {
				setState({
					input: input,
				});
			},
		setActionFilter:
			(
				actionFilter: (actions: ConversationAction[]) => ConversationAction[],
			): (({ setState }: StoreActionApi<ConversationActionStore>) => void) =>
			({ setState }) => {
				setState({
					actionFilter: actionFilter,
				});
			},
		/*
		 * Do not use this setter directly. Use setActiveAction from useActiveAction hook in conversation-assistant instead.
		 */
		setActiveAction_UNSAFE:
			(
				activeAction: ConversationAction | undefined,
			): (({ setState }: StoreActionApi<ConversationActionStore>) => void) =>
			({ setState }) => {
				setState({
					activeAction: activeAction,
				});
			},
		setActiveActionFollowUpContext:
			(
				customFollowUpContext: CustomFollowUpContext | undefined,
			): (({ getState, setState }: StoreActionApi<ConversationActionStore>) => void) =>
			({ getState, setState }) => {
				const activeAction = getState().activeAction;

				if (!activeAction || !('customFollowUpContext' in activeAction)) {
					return;
				}

				setState({
					activeAction: {
						...activeAction,
						customFollowUpContext: customFollowUpContext,
					},
				});
			},
		setButtonClickedTimestamp:
			(
				buttonClickedTimestamp: number,
			): (({ setState }: StoreActionApi<ConversationActionStore>) => void) =>
			({ setState }) => {
				setState({
					buttonClickedTimestamp: buttonClickedTimestamp,
				});
			},
		getConversationActions:
			(): (({ getState }: StoreActionApi<ConversationActionStore>) => ConversationAction[]) =>
			({ getState }) => {
				return getState().actions;
			},
		getActionFilter:
			(): (({
				getState,
			}: StoreActionApi<ConversationActionStore>) =>
				| ((actions: ConversationAction[]) => ConversationAction[])
				| undefined) =>
			({ getState }) => {
				return getState().actionFilter;
			},
		getInput:
			(): (({ getState }: StoreActionApi<ConversationActionStore>) => string | undefined) =>
			({ getState }) => {
				return getState().input;
			},
		getActiveAction:
			(): (({
				getState,
			}: StoreActionApi<ConversationActionStore>) => ConversationAction | undefined) =>
			({ getState }) => {
				return getState().activeAction;
			},
		getButtonClickedTimestamp:
			(): (({ getState }: StoreActionApi<ConversationActionStore>) => number | undefined) =>
			({ getState }) => {
				return getState().buttonClickedTimestamp;
			},
	},
});

export const useConversationActionsStore: HookFunction<
	ConversationActionStore,
	BoundActions<
		ConversationActionStore,
		{
			setConversationActions: (
				actions: ConversationAction[],
			) => ({ setState }: StoreActionApi<ConversationActionStore>) => void;
			/*
			 * Jira Issue View does not use the provider pattern yet, so we need to skip the provider action fetching.
			 * When Jira Issue View is migrated to set actions using provider pattern, we can remove this setter.
			 */
			setJiraIssueViewActions: (
				actions: ConversationAction[],
			) => ({ setState }: StoreActionApi<ConversationActionStore>) => void;
			setInput: (input: string) => ({ setState }: StoreActionApi<ConversationActionStore>) => void;
			setActionFilter: (
				actionFilter: (actions: ConversationAction[]) => ConversationAction[],
			) => ({ setState }: StoreActionApi<ConversationActionStore>) => void;
			/*
			 * Do not use this setter directly. Use setActiveAction from useActiveAction hook in conversation-assistant instead.
			 */
			setActiveAction_UNSAFE: (
				activeAction: ConversationAction | undefined,
			) => ({ setState }: StoreActionApi<ConversationActionStore>) => void;
			setActiveActionFollowUpContext: (
				customFollowUpContext: CustomFollowUpContext | undefined,
			) => ({ getState, setState }: StoreActionApi<ConversationActionStore>) => void;
			setButtonClickedTimestamp: (
				buttonClickedTimestamp: number,
			) => ({ setState }: StoreActionApi<ConversationActionStore>) => void;
			getConversationActions: () => ({
				getState,
			}: StoreActionApi<ConversationActionStore>) => ConversationAction[];
			getActionFilter: () => ({
				getState,
			}: StoreActionApi<ConversationActionStore>) =>
				| ((actions: ConversationAction[]) => ConversationAction[])
				| undefined;
			getInput: () => ({ getState }: StoreActionApi<ConversationActionStore>) => string | undefined;
			getActiveAction: () => ({
				getState,
			}: StoreActionApi<ConversationActionStore>) => ConversationAction | undefined;
			getButtonClickedTimestamp: () => ({
				getState,
			}: StoreActionApi<ConversationActionStore>) => number | undefined;
		}
	>,
	void
> = createHook(store);
export const useConversationActionsStoreActions: HookActionsFunction<
	BoundActions<
		ConversationActionStore,
		{
			setConversationActions: (
				actions: ConversationAction[],
			) => ({ setState }: StoreActionApi<ConversationActionStore>) => void;
			/*
			 * Jira Issue View does not use the provider pattern yet, so we need to skip the provider action fetching.
			 * When Jira Issue View is migrated to set actions using provider pattern, we can remove this setter.
			 */
			setJiraIssueViewActions: (
				actions: ConversationAction[],
			) => ({ setState }: StoreActionApi<ConversationActionStore>) => void;
			setInput: (input: string) => ({ setState }: StoreActionApi<ConversationActionStore>) => void;
			setActionFilter: (
				actionFilter: (actions: ConversationAction[]) => ConversationAction[],
			) => ({ setState }: StoreActionApi<ConversationActionStore>) => void;
			/*
			 * Do not use this setter directly. Use setActiveAction from useActiveAction hook in conversation-assistant instead.
			 */
			setActiveAction_UNSAFE: (
				activeAction: ConversationAction | undefined,
			) => ({ setState }: StoreActionApi<ConversationActionStore>) => void;
			setActiveActionFollowUpContext: (
				customFollowUpContext: CustomFollowUpContext | undefined,
			) => ({ getState, setState }: StoreActionApi<ConversationActionStore>) => void;
			setButtonClickedTimestamp: (
				buttonClickedTimestamp: number,
			) => ({ setState }: StoreActionApi<ConversationActionStore>) => void;
			getConversationActions: () => ({
				getState,
			}: StoreActionApi<ConversationActionStore>) => ConversationAction[];
			getActionFilter: () => ({
				getState,
			}: StoreActionApi<ConversationActionStore>) =>
				| ((actions: ConversationAction[]) => ConversationAction[])
				| undefined;
			getInput: () => ({ getState }: StoreActionApi<ConversationActionStore>) => string | undefined;
			getActiveAction: () => ({
				getState,
			}: StoreActionApi<ConversationActionStore>) => ConversationAction | undefined;
			getButtonClickedTimestamp: () => ({
				getState,
			}: StoreActionApi<ConversationActionStore>) => number | undefined;
		}
	>
> = createActionsHook(store);
export const useConversationActionsStoreState: HookStateFunction<ConversationActionStore, void> =
	createStateHook(store);

export type ConversationActionsStoreActions = ReturnType<typeof useConversationActionsStoreActions>;
